import { Button } from "@bednbody/ui/components/button";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { useToggleStaffActive } from "@/features/roster/roster.hooks";
import { useAttendance, useHubStaff } from "../ops-dashboard.hooks";

/**
 * One row per staff member: who they are, whether they're on duty right now,
 * what they're working on, and the active toggle. Replaces the dashboard's
 * former Attendance + Hub Staff cards, which listed the same people twice
 * (full views live on /attendance and /roster).
 */
export function TeamBoard({ hubId }: { hubId: string | null }) {
  const { data: attendance, isLoading: attendanceLoading } = useAttendance(hubId);
  const { data: staff, isLoading: staffLoading } = useHubStaff(hubId);
  const toggleActive = useToggleStaffActive();

  if (attendanceLoading || staffLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-12 motion-reduce:animate-none" />
        ))}
      </div>
    );
  }

  if (!staff || staff.length === 0) {
    return <p className="text-sm text-muted-foreground">No staff assigned to this hub.</p>;
  }

  const rows = staff
    .filter((s) => s.role === "therapist" || s.role === "rider")
    .map((s) => {
      const att = attendance?.find((a) => a.id === s.id);
      return {
        ...s,
        onSession: att?.attendance?.onDuty ?? false,
        startedTime: att?.startedTime ?? null,
        currentBooking: att?.currentBooking ?? null,
      };
    });

  return (
    <div className="space-y-1.5">
      {rows.map((person) => {
        const duty = !person.active
          ? { label: "Inactive", style: "bg-border text-muted-foreground" }
          : person.onSession
            ? { label: "On duty", style: "bg-success/15 text-success" }
            : { label: "Off duty", style: "bg-background text-muted-foreground" };

        const roleLabel = person.role.charAt(0).toUpperCase() + person.role.slice(1);
        const subline = person.onSession
          ? `${roleLabel}  · since ${person.startedTime}${person.currentBooking ? `, on ${person.currentBooking}` : ""}`
          : roleLabel;

        return (
          <div
            key={person.id}
            className="flex items-center justify-between gap-2 rounded-lg border border-border p-2"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{person.name}</p>
              <p className="truncate text-xs text-muted-foreground">{subline}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className={`rounded-full px-2 py-1 text-xs font-medium ${duty.style}`}>
                {duty.label}
              </span>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => toggleActive.mutate({ staffId: person.id, active: !person.active })}
                disabled={toggleActive.isPending}
              >
                {person.active ? "Deactivate" : "Activate"}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
